import { axios } from '@/plugins/axios';
import Axios from 'axios';
import { getDiscountType } from '@/util/piggyHelper';

const base = '/piggy';

let currentAbortController = null;

export async function calculateEstimatedCredits(params, cfg = {}) {
    if (currentAbortController) {
        currentAbortController.abort();
    }

    currentAbortController = new AbortController();
    const { signal } = currentAbortController;

    try {
        const response = await axios.get(`${base}/calculate-credits`, {
            params,
            signal,
            ...cfg
        });

        return response;
    } catch (error) {
        if (error.name !== 'AbortError' && !Axios.isCancel(error)) {
            throw new Error(`API ${error}`);
        }
        throw error;
    } finally {
        currentAbortController = null;
    }
}

export function getRewards(params, cfg = {}) {
    return axios.get(`${base}/rewards`, {
        params,
        ...cfg
    });
}

export function claimRewards(payload, cfg = {}) {
    return axios.post(`${base}/claim-rewards`, payload, {
        ...cfg
    });
}

export const parseDiscountType = type => {
    const prefix = type.split('_')[0];

    return {
        prefix,
        type
    };
};

export const isRewardAvailable = (
    reward,
    contact,
    items,
    categories,
    venueProducts,
    posProvider
) => {
    if (
        !contact.credit_balance ||
        contact.credit_balance.balance < reward.required_credits
    ) {
        return {
            isAvailable: false,
            selectedProduct: null
        };
    }

    const discountType = getDiscountType(reward);

    if (
        discountType === 'basket_amount' ||
        discountType === 'basket_percentage'
    ) {
        return { isAvailable: true, selectedProduct: null };
    }

    const productIds = reward?.attributes
        ? reward.attributes.storekit_plu || []
        : reward?.reward
          ? reward.reward.storekit_plu || []
          : reward?.storekit_plu || [];

    let matchingCartItems = [];

    if (
        discountType === 'category_percent_modifiers_multi' ||
        discountType === 'category_percent_modifiers_single' ||
        discountType === 'category_percent_base_single'
    ) {
        const { categoryProperty, cartItemProperty } =
            getCategoryAndCartItemProperties(posProvider);

        const matchingCategories = categories.filter(category =>
            productIds.includes(category[categoryProperty]?.toString())
        );

        matchingCartItems = matchingCategories.filter(category =>
            items.some(
                cartItem =>
                    category[categoryProperty] === cartItem[cartItemProperty]
            )
        );
    } else if (discountType === 'modifier_percent_base_single') {
        const modifiers = items.reduce((acc, carItem) => {
            const matchingModifiers = carItem.modifiers.filter(modifier =>
                productIds.includes(modifier.plu)
            );
            const uniqueModifiers = matchingModifiers.filter(
                modifier =>
                    !acc.some(accModifier => accModifier.plu === modifier.plu)
            );

            return acc.concat(uniqueModifiers);
        }, []);

        matchingCartItems = modifiers.length ? [modifiers[0]] : 0;
    } else {
        matchingCartItems = items.filter(cartItem =>
            venueProducts.some(
                product =>
                    productIds.includes(product.plu) &&
                    product.plu === cartItem.plu
            )
        );
    }

    const selectedProduct = matchingCartItems.length
        ? matchingCartItems[0].id
        : null;

    return {
        isAvailable: !!matchingCartItems.length,
        selectedProduct
    };
};

export const updateRewardAvailability = (
    rewards,
    collectableRewards,
    vouchers,
    contact,
    items,
    categories,
    venueProducts,
    posProvider
) => {
    rewards.forEach(reward => {
        const { isAvailable, selectedProduct } = isRewardAvailable(
            reward,
            contact,
            items,
            categories,
            venueProducts,
            posProvider
        );

        reward.isAvailable = isAvailable;
        reward.selectedProduct = selectedProduct;
    });

    collectableRewards.forEach(reward => {
        const { isAvailable, selectedProduct } = isRewardAvailable(
            reward,
            contact,
            items,
            categories,
            venueProducts,
            posProvider
        );

        reward.isAvailable = isAvailable;
        reward.selectedProduct = selectedProduct;
    });

    vouchers.forEach(voucher => {
        const { isAvailable, selectedProduct } = isRewardAvailable(
            voucher,
            contact,
            items,
            categories,
            venueProducts,
            posProvider
        );

        voucher.isAvailable = isAvailable;
        voucher.selectedProduct = selectedProduct;
    });
};

export const getProductsToDiscount = (
    reward,
    product,
    discountType,
    items,
    categories,
    posProvider
) => {
    const { categoryProperty, cartItemProperty } =
        getCategoryAndCartItemProperties(posProvider);

    if (discountType.prefix === 'category') {
        const categoryPosId = categories.find(
            category => category.id === reward.selectedProduct
        )?.[categoryProperty];

        return items.filter(
            cartItem => cartItem[cartItemProperty] === categoryPosId
        );
    } else {
        return [product];
    }
};

export const calculateProductPrice = (product, quantity, discountType) => {
    const isMulti = discountType.type.includes('multi');
    const isModifiers = discountType.type.includes('modifiers');
    const basePrice = product.price * (isMulti ? quantity : 1);
    const modifiersPrice = isModifiers
        ? isMulti
            ? sumModifiersPrice(product) * quantity
            : sumModifiersPrice(product)
        : 0;
    const productPrice = basePrice + modifiersPrice;

    return { productPrice };
};

export const calculateDiscount = (
    productPrice,
    discountValue,
    discountType,
    paymentAmount = null
) => {
    let discount = 0;

    if (
        discountType.type.includes('product_percent') ||
        discountType.type.includes('category_percent')
    ) {
        discount = Math.min(productPrice * (discountValue / 100), productPrice);
    }

    if (discountType.type.includes('product_amount')) {
        discount = Math.min(discountValue * 100, productPrice);
    }

    if (paymentAmount !== null) {
        discount = Math.min(discount, paymentAmount);
    }

    return discount;
};

export const sumModifiersPrice = product => {
    if (product.modifiers.length) {
        return product.modifiers.reduce(
            (total, modifier) =>
                total + modifier.price * (modifier.quantity || 1),
            0
        );
    }

    return 0;
};

export const getCategoryAndCartItemProperties = posProvider => {
    let categoryProperty;
    let cartItemProperty;

    switch (posProvider) {
        case 'deliverect':
            categoryProperty = 'deliverectId';
            cartItemProperty = 'categoryPosId';
            break;
        case 'ikentoo':
            categoryProperty = 'posId';
            cartItemProperty = 'categoryPosId';
            break;
        default:
            categoryProperty = 'id';
            cartItemProperty = 'categoryId';
            break;
    }

    return { categoryProperty, cartItemProperty };
};

export const findItemById = (productId, items) => {
    return items.find(carItem => carItem.id === productId);
};
